import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  DollarSign, 
  Calendar,
  User,
  Building,
  CreditCard,
  History
} from 'lucide-react';
import api from '../utils/axios';
import { toastErrorOnce } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { canEditSalaries } from '../utils/compensationPermissions';
import { formatDateOnly } from '../utils/dateUtils';

const SalaryDetail = () => {
  const { hasPermission } = useAuth();
  // Same gate as EditSalary: salaries:update_own (held by every employee) is not enough.
  const canEdit = canEditSalaries(hasPermission);
  const { id } = useParams();
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSalary = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/salaries/${id}`);
        setSalary(response.data.data);
      } catch (error) {
        console.error('Error fetching salary:', error);
        // Fixed ids: StrictMode loads twice in dev, and each failure would toast again.
        if (error.response?.status === 404) {
          toastErrorOnce('Salary record not found');
        } else if (error.response?.status === 403) {
          toastErrorOnce('You do not have permission to view this salary record');
        } else {
          toastErrorOnce('Failed to load salary details');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSalary();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!salary) {
    return (
      <div className="text-center py-12">
        <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Salary record not found</h3>
        <p className="mt-1 text-sm text-gray-500">The salary record you're looking for doesn't exist.</p>
        <Link
          to="/salaries"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-100 hover:bg-primary-200"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Salaries
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/salaries"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Salary Record Details</h1>
            <p className="text-gray-600">
              {salary.first_name} {salary.last_name} - {salary.emp_id}
            </p>
          </div>
        </div>
        <Link
          to={canEdit ? `/salaries/${id}/edit` : '#'}
          data-testid="salary-detail-edit"
          aria-disabled={!canEdit}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
            canEdit
              ? 'text-white bg-primary-600 hover:bg-primary-700'
              : 'text-gray-400 bg-gray-300 cursor-not-allowed'
          }`}
          title={canEdit ? 'Edit Salary' : 'You do not have permission to edit salaries'}
          onClick={(e) => {
            if (!canEdit) {
              e.preventDefault();
            }
          }}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Salary
        </Link>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Salary Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Salary Information */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Salary Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Amount & Currency */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">{salary.currency} Salary</h3>
                <div className="flex items-center space-x-3 mb-2">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(salary.amount, salary.currency)}
                    </p>
                  </div>
                </div>
                {salary.effective_date && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>Effective: {formatDateOnly(salary.effective_date)}</span>
                  </div>
                )}
              </div>

              {/* Payment & Bank Details */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Payment Details</h3>
                {salary.payment_method_name && (
                  <div className="flex items-center space-x-3 mb-2">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{salary.payment_method_name}</p>
                    </div>
                  </div>
                )}
                {salary.account_number && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Account:</span> {salary.account_number}
                  </div>
                )}
                {salary.account_title && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Account Title:</span> {salary.account_title}
                  </div>
                )}
                {salary.bank_name && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Bank:</span> {salary.bank_name}
                  </div>
                )}
                {!salary.payment_method_name && !salary.account_number && !salary.bank_name && (
                  <p className="text-gray-400 text-sm">No payment details available</p>
                )}
              </div>
            </div>

            {/* Notes */}
            {salary.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Notes:</span> {salary.notes}
                </p>
              </div>
            )}
          </div>

          {/* Salary History */}
          {salary.history && Array.isArray(salary.history) && salary.history.length > 0 && (
            <div className="bg-white rounded-lg shadow-soft p-6">
              <div className="flex items-center space-x-2 mb-4">
                <History className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Salary History</h2>
                <span className="text-sm text-gray-500">({salary.history.length} change{salary.history.length !== 1 ? 's' : ''})</span>
              </div>
              <div className="space-y-4">
                {salary.history.map((entry, index) => {
                  if (entry.deleted) {
                    return (
                      <div key={index} className="border-l-4 border-red-500 pl-4 py-2 bg-red-50 rounded">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-red-900">Record Deleted</p>
                            {entry.deleted_at && (
                              <p className="text-xs text-red-700">
                                {new Date(entry.deleted_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={index} className="border-l-4 border-primary-500 pl-4 py-3 bg-gray-50 rounded">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(entry.amount, entry.currency || salary.currency)}
                            </span>
                            <span className="text-xs text-gray-500">({entry.currency || salary.currency})</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            {entry.payment_method && (
                              <div><span className="font-medium">Payment Method:</span> {entry.payment_method}</div>
                            )}
                            {entry.effective_date && (
                              <div><span className="font-medium">Effective Date:</span> {formatDateOnly(entry.effective_date)}</div>
                            )}
                            {entry.notes && (
                              <div><span className="font-medium">Notes:</span> {entry.notes}</div>
                            )}
                            {entry.created_at && (
                              <div className="text-gray-500">
                                Changed on {new Date(entry.created_at).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Employee Information */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Employee Name</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {salary.first_name} {salary.last_name}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Employee ID</p>
                  <p className="text-lg font-semibold text-gray-900">{salary.emp_id}</p>
                </div>
              </div>
              
              {salary.department && (
                <div className="flex items-center space-x-3">
                  <Building className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Department</p>
                    <p className="text-lg font-semibold text-gray-900">{salary.department}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Link
                to={`/employees/${salary.employee_id}`}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <User className="h-4 w-4 mr-2" />
                View Employee Details
              </Link>
              <Link
                to={`/salaries?employee_id=${salary.employee_id}`}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                View All Salaries for Employee
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryDetail;
